import { useReducer } from "react";
import { useTranslation } from "react-i18next";
import { memberSubject, PermissionService } from "src/services/permissionService/permissionService";

import { AddMemberDialog } from "@dashboard/shared/components/AddMemberDialog";
import { PrimaryOutlinedButton } from "@shared/CustomButton";
import { SectionField, SectionFields } from "@shared/SectionPanel/SectionField";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";

import { useCurrentSubscriptionExpired, useCurrentWorkspace } from "../../api/helpers";

type PendingInvitationsPanelProps = {
    workspacePendingCount: number;
    groupPendingCount: number;
};

export function PendingInvitationsPanel(props: PendingInvitationsPanelProps) {
    const { t } = useTranslation(["common", "pages"]);
    const [dialogOpen, toggleDialog] = useReducer((state) => !state, false);

    const isExpired = useCurrentSubscriptionExpired();
    const { data: workspace } = useCurrentWorkspace();
    const { workspaceAbility } = PermissionService();
    const ability = workspaceAbility(workspace);
    const mayInviteMember = ability.can("create", "Member");

    return (
        <SectionPanel
            title={t("pages:workspacePage.section.title.pendingInvitations")}
            actions={
                <PrimaryOutlinedButton
                    disabled={!mayInviteMember}
                    onClick={toggleDialog}
                    tooltipProps={{
                        hide: mayInviteMember,
                        title: isExpired
                            ? t("pages:workspacePage.tooltip.subscriptionExpired")
                            : t("pages:workspacePage.tooltip.cannotManageMembers"),
                    }}
                    name="workspaces-invite-member"
                    startIcon={<i className="fa-solid fa-plus" />}
                >
                    {t("pages:workspacePage.button.inviteMember")}
                </PrimaryOutlinedButton>
            }
        >
            <SectionFields>
                <SectionField
                    label={t("pages:workspacePage.pendingInvitations.workspace")}
                    value={String(props.workspacePendingCount)}
                />
                <SectionField
                    label={t("pages:workspacePage.pendingInvitations.group")}
                    value={String(props.groupPendingCount)}
                />
            </SectionFields>

            {workspace ? (
                <AddMemberDialog
                    id={workspace.id}
                    name="workspace"
                    canAssignOwner={ability.can("manage", memberSubject("Owner"))}
                    open={dialogOpen}
                    onClose={toggleDialog}
                />
            ) : null}
        </SectionPanel>
    );
}
